import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { Add, ArrowBack, Comment, Save } from '@mui/icons-material'
import { Alert, Box, Button, ButtonBase, Card, CardContent, Grid, Link, Skeleton, Stack, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { Game } from '../../../features/game/types/Game'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useGetInternalFeatureMapQuery } from '../../api/features'
import { useGetBulkAssignGamesQuery, useMatchBulkAssignConceptsMutation, useUnmatchBulkAssignConceptsMutation } from '../../api/implementation-bulk-assign'
import { useGetInternalScreenshotsByIdsQuery } from '../../api/implementation-examples'
import { useGetInternalConceptsQuery, useGetInternalTagsQuery } from '../../api/tags'
import { AddFeaturesDialog } from '../../components/AddFeaturesDialog/AddFeaturesDialog'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog/UnsavedChangesDialog'
import { ConceptRow, ScreenshotFeatureList } from '../../features/analysis-screenshots/components/ScreenshotFeatureList/ScreenshotFeatureList'
import { Notice, WriteNotice } from '../../features/analysis-screenshots/components/WriteNotice/WriteNotice'
import {
  getConceptMatchGroups,
  getConceptUnmatchGroup,
  toConceptsById,
  toStoredFeatureRows,
} from '../../features/analysis-screenshots/helpers/screenshotHelpers'
import { getConceptsReadBackProblem, Message, toPositions } from '../../features/analysis-screenshots/helpers/writeMessages'
import { ScreenshotEnlargeDialog } from '../../features/implementation-bulk-assign/components/ScreenshotEnlargeDialog/ScreenshotEnlargeDialog'
import { readBulkAssignEdit } from '../../features/implementation-bulk-assign/helpers/bulkAssignEditSelection'
import {
  bulkAssignMarketIso,
  formatReleaseDate,
  getScreenshotGameIds,
  toEditView,
  toFeatureMapByLegacyId,
} from '../../features/implementation-bulk-assign/helpers/bulkAssignHelpers'
import { implementationBulkAssignPath } from '../../features/implementation-bulk-assign/helpers/bulkAssignPaths'
import { BulkAssignEditState, BulkAssignEditView } from '../../features/implementation-bulk-assign/types/ImplementationBulkAssign'
import { ScreenshotThumbnail } from '../../features/implementation-examples/components/ScreenshotThumbnail/ScreenshotThumbnail'
import { defaultImplementationMarketIso, getScreenshotCommentEditPath } from '../../features/implementation-examples/helpers/implementationExamplePaths'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { InternalTagType } from '../../types/InternalTag'

// "Select all" can send a thousand screenshots here; the cards come in pages, while saving still covers every screenshot.
const editCardsPerPage = 60

export const ImplementationBulkAssignEditPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-implementation-bulk-assign-edit'))
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const state = (location.state ?? {}) as BulkAssignEditState
  const hasState = Array.isArray(state.screenshotIds)
  const selection = searchParams.get('selection')
  const stored = useMemo(() => (hasState ? undefined : readBulkAssignEdit(selection)), [hasState, selection])
  const screenshotIds = useMemo(
    () => (hasState ? (state.screenshotIds ?? []).filter((id) => typeof id === 'string') : stored?.screenshotIds ?? []),
    [hasState, state.screenshotIds, stored]
  )
  const back = () => {
    if (state.fromList) navigate(-1)
    else if (stored?.returnTo) navigate(stored.returnTo, { state: stored.listState })
    else navigate(implementationBulkAssignPath)
  }

  if (!screenshotIds.length) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Typography color="error.main">{t('internal-analysts:ie_bulk_assign_edit_no_screenshots')}</Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(implementationBulkAssignPath)}>
          {t('internal-analysts:ie_bulk_assign_back_to_start')}
        </Button>
      </Stack>
    )
  }
  return <BulkAssignEdit key={screenshotIds.join(',')} screenshotIds={screenshotIds} onBack={back} />
}

const EditCard: FC<{ view: BulkAssignEditView; game?: Game; onHover: (id?: string) => void; onEnlarge: () => void }> = ({ view, game, onHover, onEnlarge }) => {
  const { t } = useTranslation()
  const { screenshot } = view
  const gameName = game?.resolvedName || game?.name || screenshot.gameId
  const version = [screenshot.gameVersion || '-', formatReleaseDate(screenshot.versionReleaseDate)].filter(Boolean).join(' - ')
  return (
    <Card
      variant="outlined"
      onMouseEnter={() => onHover(screenshot.id)}
      onMouseLeave={() => onHover(undefined)}
      data-testid={`bulk-assign-edit-${screenshot.id}`}
    >
      <ButtonBase
        sx={{ display: 'block', width: '100%' }}
        aria-label={t('internal-analysts:ie_bulk_assign_enlarge_label', { game: gameName, version })}
        onFocus={() => onHover(screenshot.id)}
        onBlur={() => onHover(undefined)}
        onClick={onEnlarge}
      >
        <ScreenshotThumbnail url={screenshot.url} alt="" height={200} />
      </ButtonBase>
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        <Typography variant="body2" fontWeight="bold" noWrap title={gameName}>
          {gameName}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {t('internal-analysts:analysis_screenshots_version', { version })}
        </Typography>
        <Link
          variant="caption"
          href={getScreenshotCommentEditPath({ gameId: screenshot.gameId, screenshotIds: [screenshot.id], marketIso: defaultImplementationMarketIso })}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          <Comment sx={{ fontSize: 14 }} />
          {t(screenshot.commentId ? 'internal-analysts:implementation_comment_edit' : 'internal-analysts:implementation_comment_add')}
        </Link>
      </CardContent>
    </Card>
  )
}

const BulkAssignEdit: FC<{ screenshotIds: string[]; onBack: () => void }> = ({ screenshotIds, onBack }) => {
  const { t } = useTranslation()
  const { notifySuccess } = useMutationFeedback()
  const screenshotsQuery = useGetInternalScreenshotsByIdsQuery(screenshotIds, { refetchOnMountOrArgChange: true })
  const featureMapQuery = useGetInternalFeatureMapQuery({ marketIso: bulkAssignMarketIso })
  const conceptsQuery = useGetInternalConceptsQuery()
  const tagsQuery = useGetInternalTagsQuery()
  const [matchConcepts, matchState] = useMatchBulkAssignConceptsMutation()
  const [unmatchConcepts, unmatchState] = useUnmatchBulkAssignConceptsMutation()
  const [pendingConceptIds, setPendingConceptIds] = useState<string[]>([])
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [removeRequest, setRemoveRequest] = useState<ConfirmDialogData<ConceptRow>>()
  const [hoveredId, setHoveredId] = useState<string>()
  const [enlargedUrl, setEnlargedUrl] = useState<string>()
  const [notice, setNotice] = useState<Notice>()
  const [shownCount, setShownCount] = useState(editCardsPerPage)

  const screenshots = useMemo(() => {
    const byId = new Map((screenshotsQuery.data ?? []).map((screenshot) => [screenshot.id, screenshot]))
    return screenshotIds.flatMap((id) => {
      const screenshot = byId.get(id)
      return screenshot ? [screenshot] : []
    })
  }, [screenshotsQuery.data, screenshotIds])
  const gameIds = useMemo(() => getScreenshotGameIds(screenshots), [screenshots])
  const gamesQuery = useGetBulkAssignGamesQuery(gameIds, { skip: !gameIds.length })
  const gamesById = useMemo(() => new Map((gamesQuery.data ?? []).map((game) => [game.id, new Game(game)])), [gamesQuery.data])
  const conceptsById = useMemo(
    () =>
      toConceptsById(
        conceptsQuery.data ?? [],
        (tagsQuery.data ?? []).filter((tag) => tag.type === InternalTagType.Concept)
      ),
    [conceptsQuery.data, tagsQuery.data]
  )
  const views = useMemo(() => {
    const featuresByLegacyId = toFeatureMapByLegacyId(featureMapQuery.data)
    return screenshots.map((screenshot) => toEditView(screenshot, featuresByLegacyId, conceptsById))
  }, [screenshots, featureMapQuery.data, conceptsById])
  const items = useMemo(() => views.map(({ item }) => item), [views])
  const stored = useMemo(
    () =>
      toStoredFeatureRows(
        views,
        views.find((view) => view.item.screenshotId === hoveredId)
      ),
    [views, hoveredId]
  )
  const isDirty = pendingConceptIds.length > 0
  const { guard } = useEditPageLeave(isDirty)
  const isBusy = matchState.isLoading || unmatchState.isLoading || screenshotsQuery.isFetching
  const positions = toPositions(screenshots.map(({ id }) => id))
  const missingCount = screenshotIds.length - screenshots.length

  const conceptRows: ConceptRow[] = [
    ...stored.concepts,
    ...pendingConceptIds.map((id) => ({ concept: conceptsById.get(id) ?? { id, name: id, featureTagId: null }, pending: true })),
  ]
  const conceptsOnEvery = views.length ? views[0].item.conceptIds.filter((id) => views.every((view) => view.item.conceptIds.includes(id))) : []

  const showMessages = (severity: Notice['severity'], messages: Message[]) => setNotice({ severity, messages })

  const save = async () => {
    const groups = getConceptMatchGroups(items, pendingConceptIds)
    if (groups.length) {
      try {
        const { readBack } = await matchConcepts({ groups }).unwrap()
        const problem = getConceptsReadBackProblem(readBack, positions, true)
        // Kept for a retry, which matches each concept only to the screenshots still lacking it.
        if (problem) {
          showMessages('error', [{ key: 'internal-analysts:analysis_screenshots_not_stored' }, problem])
          return false
        }
      } catch {
        showMessages('error', [{ key: 'internal-analysts:analysis_screenshots_match_failed' }])
        return false
      }
    }
    setPendingConceptIds([])
    setNotice(undefined)
    notifySuccess(t('internal-analysts:ie_bulk_assign_concepts_saved'))
    return true
  }

  const removeStoredConcept = async ({ concept }: ConceptRow) => {
    const group = getConceptUnmatchGroup(items, concept.id)
    if (!group) return
    try {
      const { readBack } = await unmatchConcepts({ group }).unwrap()
      const problem = getConceptsReadBackProblem(readBack, positions, false)
      if (problem) showMessages('error', [problem])
      else notifySuccess(t('internal-analysts:analysis_screenshots_concept_removed'))
    } catch {
      showMessages('error', [{ key: 'internal-analysts:analysis_screenshots_unmatch_failed' }])
    }
  }

  const askRemove = (row: ConceptRow) => {
    if (row.pending) {
      setPendingConceptIds((ids) => ids.filter((id) => id !== row.concept.id))
      return
    }
    setRemoveRequest({
      title: t('internal-analysts:analysis_screenshots_remove_concept_title'),
      confirmText: t('internal-analysts:analysis_screenshots_remove_concept_confirm', { name: row.concept.name, count: row.count ?? 1 }),
      actionText: t('internal-analysts:analysis_screenshots_remove'),
      destructiveAction: true,
      data: row,
    })
  }

  const isLoading = screenshotsQuery.isLoading || featureMapQuery.isLoading || conceptsQuery.isLoading || tagsQuery.isLoading
  const loadFailed = screenshotsQuery.isError || featureMapQuery.isError || conceptsQuery.isError || tagsQuery.isError

  return (
    <Box pb={8}>
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap mb={2}>
        <Button size="small" startIcon={<ArrowBack />} onClick={onBack}>
          {t('internal-analysts:ie_bulk_assign_back')}
        </Button>
        <Typography variant="h5" fontWeight="bold">
          {t('internal-analysts:analysis_screenshots_edit_multiple_title', { count: screenshots.length })}
        </Typography>
        <Stack direction="row" spacing={1} ml="auto">
          <Button variant="outlined" startIcon={<Add />} disabled={isBusy || isLoading || !screenshots.length} onClick={() => setIsDialogOpen(true)}>
            {t('internal-analysts:add_concepts_title')}
          </Button>
          <Button variant="contained" startIcon={<Save />} disabled={isBusy || !isDirty} onClick={save}>
            {t('internal-common:save')}
          </Button>
        </Stack>
      </Stack>
      <Alert severity="info" role="note" sx={{ mb: 2 }}>
        {t('internal-analysts:ie_bulk_assign_customer_notice')}
      </Alert>
      <WriteNotice notice={notice} onClose={() => setNotice(undefined)} />
      {loadFailed && (
        <Typography color="error.main" mb={2}>
          {t('internal-analysts:ie_bulk_assign_edit_load_failed')}
        </Typography>
      )}
      {missingCount > 0 && !isLoading && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('internal-analysts:implementation_comment_screenshots_missing', { count: missingCount })}
        </Alert>
      )}
      {isLoading ? (
        <Skeleton variant="rectangular" height={480} />
      ) : (
        <Grid container spacing={3} wrap="wrap">
          <Grid item xs={12} md={7}>
            <Grid container spacing={1} wrap="wrap">
              {views.slice(0, shownCount).map((view) => (
                <Grid item xs={6} sm={4} key={view.item.screenshotId}>
                  <EditCard
                    view={view}
                    game={gamesById.get(view.screenshot.gameId)}
                    onHover={setHoveredId}
                    onEnlarge={() => setEnlargedUrl(view.screenshot.url)}
                  />
                </Grid>
              ))}
            </Grid>
            {shownCount < views.length && (
              <Button sx={{ mt: 1 }} onClick={() => setShownCount((count) => count + editCardsPerPage)}>
                {t('internal-analysts:implementation_show_more', { shown: shownCount, total: views.length })}
              </Button>
            )}
          </Grid>
          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
              <CardContent>
                {views.length > 1 && (
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {t('internal-analysts:analysis_screenshots_hover_help')}
                  </Typography>
                )}
                <ScreenshotFeatureList
                  features={stored.features}
                  concepts={conceptRows}
                  unknownConceptIds={stored.unknownConceptIds}
                  total={views.length}
                  disabled={isBusy}
                  onRemoveConcept={askRemove}
                />
                {isDirty && (
                  <Typography variant="body2" color="warning.main" mt={2}>
                    {t('internal-analysts:ie_bulk_assign_pending_help')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <AddFeaturesDialog
        open={isDialogOpen}
        addedConceptIds={[...conceptsOnEvery, ...pendingConceptIds]}
        onClose={() => setIsDialogOpen(false)}
        onAdd={({ conceptIds }) => {
          setIsDialogOpen(false)
          setPendingConceptIds((current) => [...current, ...conceptIds.filter((id) => !current.includes(id))])
        }}
      />
      <ConfirmDialog
        open={!!removeRequest}
        confirmDialogData={removeRequest}
        onClose={(confirmed) => {
          setRemoveRequest(undefined)
          if (confirmed?.data) removeStoredConcept(confirmed.data)
        }}
      />
      <ScreenshotEnlargeDialog url={enlargedUrl} alt={t('internal-analysts:ie_bulk_assign_enlarged')} onClose={() => setEnlargedUrl(undefined)} />
      <UnsavedChangesDialog
        guard={guard}
        onDiscard={() => {
          setPendingConceptIds([])
          guard.proceed()
        }}
        onSave={async () => {
          if (await save()) guard.proceed()
          else guard.cancel()
        }}
      />
    </Box>
  )
}
